/**
 * Event landing-page template — 04-charity ("Benefiz-Dinner").
 *
 * Distinct CraftJS home-page document for the `charity` theme. Cloned from the
 * proven `webinar` template structure, restyled to the "naturell-barn" look:
 * warm cream surface, forest-green accent, earthy/organic, serif headers, soft
 * rounding. Authored from the design spec in
 *   TEMPLATES - LANDINPAGES/04-charity/sections/*.html (CRAFT_PROPS_HINT blocks).
 *
 * Convention (mirrors webinar.ts):
 *  - Section-level marketing copy → td() keys (`cha*` prefix) → localized.
 *  - Repetitive structured child data (agenda rows, patrons, FAQ, …) also go
 *    through td() (Wave 3b i18n); only proper names of fictitious people/acts
 *    and bare numbers stay inline.
 *  - Only sections with their own surface (Hero/Testimonials) set per-node colors.
 */
import { defaultRegistrationConsents, toCheckboxItems } from "@/lib/website/registrationConsents";
import {
  A,
  enc,
  pv,
  tc,
  btn,
  fi,
  acceptanceFi,
  scaffold,
  emptyFieldsGroup,
  PAD,
  DIST_CARD,
  BTN_FORM_SP,
  SEC_MARGIN,
  ACCEPTANCE_VISIBILITY_FILTER,
  type N,
  type TranslationFn,
} from "../temp";
import { EASE, decorLayer, fadeUpKeyframes, fontFaces, heroShell, motionGate, revealBlock } from "./premium-css";
import type { WebsiteThemeStylePreset } from "@/types/WebsiteTheme";
import {
  SAMPLE_SPEAKER_IMAGES,
  SAMPLE_SPONSOR_LOGOS,
} from "@/lib/website-builder/sampleContent";

// Charity-specific asset namespace (seeded under <CDN>/charity/…)
const W = `${A}/shared/templates/04-charity`;

const ACCENT = "#3d5a3d"; // forest green
const INK = "#2a2419"; // warm near-black headings
const MUTE = "#6b6353"; // earthy muted body
const DARK = "#2a2419"; // dark section surface
const ON_DARK = "#d9d2c4"; // body text on dark
const ON_DARK_ACCENT = "#c4d5b8"; // eyebrow on dark
const SERIF = "'Cormorant Garamond', Cormorant, Georgia, 'Times New Roman', serif";

// Speaker/testimonial faces come from the shared sample rotation
// (lib/website-builder/sampleContent); the template's own `<family>/*`
// art stays what it is: heroes, details and gallery photography.
const LOGO = `${A}/shared/logo.svg`;

// ── Child data (td-localized; proper names + bare numbers stay inline) ─

const NUMBERS_DATA = (td: TranslationFn) => [
  { num: "<b>184k €</b>", desc: td("chaNum1Desc") },
  { num: "<b>47</b>", desc: td("chaNum2Desc") },
  { num: "<b>92</b>", desc: td("chaNum3Desc") },
  { num: "<b>100 %</b>", desc: td("chaNum4Desc") },
];

const AGENDA_DATA = (td: TranslationFn) => [
  { title: td("chaAgendaRow1Title"), speaker: td("chaAgendaRow1Who"), desc: td("chaAgendaRow1Desc") },
  { title: td("chaAgendaRow2Title"), speaker: td("chaAgendaRow2Who"), desc: td("chaAgendaRow2Desc") },
  { title: td("chaAgendaRow3Title"), speaker: td("chaAgendaRow3Who"), desc: td("chaAgendaRow3Desc") },
  { title: td("chaAgendaRow4Title"), speaker: "Marta Riesz", desc: td("chaAgendaRow4Desc") },
  { title: td("chaAgendaRow5Title"), speaker: "Trio Wildhaus", desc: td("chaAgendaRow5Desc") },
];

const SPEAKER_DATA = (td: TranslationFn) => [
  { name: "Hanna Naturell", role: td("chaSpeaker1Role"), img: "03" },
  { name: "Lukas Brenner", role: td("chaSpeaker2Role"), img: "06" },
  { name: "Marta Riesz", role: td("chaSpeaker3Role"), img: "05" },
  { name: "Dr. Anna Reichmann", role: td("chaSpeaker4Role"), img: "12" },
  { name: "Pfarrer Paul Linde", role: td("chaSpeaker5Role"), img: "01" },
  { name: "Trio Wildhaus", role: td("chaSpeaker6Role"), img: "04" },
];

const TESTIMONIAL_DATA = (td: TranslationFn) => [
  { quote: td("chaTesti1Quote"), name: "Marta Riesz", role: td("chaTesti1Role") },
  { quote: td("chaTesti2Quote"), name: "Stephan Wagner", role: td("chaTesti2Role") },
  { quote: td("chaTesti3Quote"), name: "Yara Costa", role: td("chaTesti3Role") },
];

const BLOG_DATA = (td: TranslationFn) => [
  { topic: `<b>${td("chaBlog1Topic")}</b>`, content: td("chaBlog1Content") },
  { topic: `<b>${td("chaBlog2Topic")}</b>`, content: td("chaBlog2Content") },
  { topic: `<b>${td("chaBlog3Topic")}</b>`, content: td("chaBlog3Content") },
];

const FAQ_DATA = (td: TranslationFn) => [
  { q: `<b>${td("chaFaq1Question")}</b>`, a: td("chaFaq1Answer") },
  { q: `<b>${td("chaFaq2Question")}</b>`, a: td("chaFaq2Answer") },
  { q: `<b>${td("chaFaq3Question")}</b>`, a: td("chaFaq3Answer") },
  { q: `<b>${td("chaFaq4Question")}</b>`, a: td("chaFaq4Answer") },
  { q: `<b>${td("chaFaq5Question")}</b>`, a: td("chaFaq5Answer") },
  { q: `<b>${td("chaFaq6Question")}</b>`, a: td("chaFaq6Answer") },
];

// ── Charity-specific child factories ──────────────────────────────────

function numbersChild(parent: string, i: number, td: TranslationFn): N {
  const d = NUMBERS_DATA(td)[i];
  return {
    type: { resolvedName: "NumbersChild" }, isCanvas: false,
    props: {
      isVisible: true,
      isVisibleNumber: true, textNumber: d.num, fontSizeNumber: "text-4xl", textAlignNumber: "center",
      isVisibleDescription: true, textDescription: d.desc, fontSizeDescription: "text-sm", textAlignDescription: "center",
    },
    displayName: "NumbersChild", custom: { icon: "IconNumber" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function agendaChild(parent: string, i: number, td: TranslationFn): N {
  const d = AGENDA_DATA(td)[i];
  return {
    type: { resolvedName: "AgendaChild" }, isCanvas: false,
    props: {
      isVisibleText1: true, textText1: `<b>${td("chaAgendaTimePlaceholder")}</b>`, fontSizeText1: "text-sm", textAlignText1: "left",
      isVisibleText2: true, textText2: d.title, fontSizeText2: "text-sm", textAlignText2: "left",
      isVisibleText3: true, textText3: d.speaker, fontSizeText3: "text-sm", textAlignText3: "left",
      isVisibleText: true, textText: d.desc, fontSizeText: "text-sm", textAlignText: "left", border: true,
    },
    displayName: "AgendaChild", custom: { icon: "IconCalendar" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function speakerChild(parent: string, i: number, td: TranslationFn): N {
  const d = SPEAKER_DATA(td)[i];
  return {
    type: { resolvedName: "SpeakerChild" }, isCanvas: false,
    props: {
      isVisible: true, imageUrl: SAMPLE_SPEAKER_IMAGES[i % SAMPLE_SPEAKER_IMAGES.length], imageBottomBackground: "transparent",
      isVisibleName: true, textName: d.name, fontSizeName: "text-lg", textAlignName: "left",
      isVisibleOccupation: true, textOccupation: d.role, fontSizeOccupation: "text-sm", textAlignOccupation: "left",
      icons: [{ icon: "IconMail" }, { icon: "IconWorld" }], isVisibleIcon: true,
    },
    displayName: "SpeakerChild", custom: { icon: "IconMicrophone" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function testimonialChild(parent: string, i: number, td: TranslationFn): N {
  const d = TESTIMONIAL_DATA(td)[i];
  return {
    type: { resolvedName: "TestimonialsChild" }, isCanvas: false,
    props: {
      isVisible: true, imageUrl: SAMPLE_SPONSOR_LOGOS[i % SAMPLE_SPONSOR_LOGOS.length].src,
      imageUrlBottom: SAMPLE_SPEAKER_IMAGES[i % SAMPLE_SPEAKER_IMAGES.length],
      isVisibleImageTop: false, imageBottomBackground: "transparent", isVisibleImageBottom: false,
      isVisibleContent: true, textContent: d.quote, fontSizeContent: "text-md", textAlignContent: "left", fontColorContent: ON_DARK,
      isVisibleName: true, textName: d.name, fontSizeName: "text-lg", textAlignName: "left", fontColorName: "#ffffff",
      isVisibleOccupation: true, textOccupation: d.role, fontSizeOccupation: "text-sm", textAlignOccupation: "left", fontColorOccupation: ON_DARK,
    },
    displayName: "TestimonialsChild", custom: { icon: "IconThumbUp" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function blogChild(parent: string, i: number, td: TranslationFn): N {
  const d = BLOG_DATA(td)[i];
  return {
    type: { resolvedName: "BlogChild" }, isCanvas: false,
    props: {
      iconAlignment: "left", isVisible: true,
      isVisibleTopic: true, textTopic: d.topic, fontSizeTopic: "text-lg", textAlignTopic: "left",
      isVisibleContent: true, textContent: d.content, fontSizeContent: "text-md", textAlignContent: "left",
      isVisibleButton: false, size: "small", text: td("learnMore"), underline: false, border: true,
    },
    displayName: "BlogChild", custom: { icon: "IconArticle" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function faqChild(parent: string, i: number, td: TranslationFn): N {
  const d = FAQ_DATA(td)[i];
  return {
    type: { resolvedName: "FaQChild" }, isCanvas: false,
    props: {
      isVisibleQuestion: true, textQuestion: d.q, fontSizeQuestion: "text-sm", textAlignQuestion: "left",
      isVisibleAnswer: true, textAnswer: d.a, fontSizeAnswer: "text-sm", textAlignAnswer: "left", border: true,
    },
    displayName: "FaQChild", custom: { icon: "IconMessageQuestion" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

const GALLERY_IMG = [
  `${W}/hero-einladung.jpg`, `${W}/detail-napkin-stack.jpg`, `${W}/hero-dokumente.jpg`,
  `${W}/hero-treatment.jpg`, `${W}/detail-wildflowers.jpg`, `${W}/hero-absage.jpg`,
];

function galleryImage(parent: string, i: number): N {
  return {
    type: { resolvedName: "ImageChild" }, isCanvas: false,
    props: { src: GALLERY_IMG[i % GALLERY_IMG.length], isVisible: true, aspectRatio: "aspect-video", displayName: "ImageChild", width: "w-full", canDuplicate: true },
    displayName: "ImageChild", custom: { icon: "IconSection" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

// ── Template ──────────────────────────────────────────────────────────

export const charity = (td: TranslationFn): string => {
  const B = "cha_body";

  const HERO = "cha_hero";
  const NUMBERS = "cha_numbers";
  const AGENDA = "cha_agenda";
  const SPEAKER = "cha_speaker";
  const TEXT = "cha_text";
  const IMAGE = "cha_image";
  const COUNTDOWN = "cha_countdown";
  const GALLERY = "cha_gallery";
  const VIDEO = "cha_video";
  const TESTI = "cha_testimonials";
  const BLOG = "cha_blog";
  const FAQ = "cha_faq";
  const MAP = "cha_map";
  const DI = "cha_datainput";
  const CONTACT = "cha_contact";

  return enc({
    ...scaffold(
      { ab: "cha_ab", abs: "cha_abs", body: B, ft: "cha_ft", fts: "cha_fts" },
      [HERO, NUMBERS, DI, AGENDA, SPEAKER, TEXT, IMAGE, COUNTDOWN, GALLERY, VIDEO, TESTI, BLOG, FAQ, MAP, CONTACT],
      false, { background: "bg-white", padding: "p-4", borderRadius: "rounded-lg" },
    ),

    // ── Hero (image right, dark text on cream) ──
    [HERO]: {
      type: { resolvedName: "Hero" }, isCanvas: false,
      props: {
        sectionHeight: 85, contentAlignment: "center", imageLayout: "right", layoutWidth: "50%", inversed: true,
        padding: PAD, distButtons: "gap-3 wb-md:gap-4", distContentImage: "gap-6 wb-md:gap-10 wb-lg:gap-16", distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButtons: "gap-6 wb-md:gap-8", buttonAlignment: "flex-row", isVisible: true,
        isVisibleHeadline: true, textHeadline: td("chaHeroEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "left", headlineColor: ACCENT,
        isVisibleHeader: true, textHeader: td("chaHeroHeader"), fontSizeHeader: "text-6xl", textAlignHeader: "left", headerColor: INK,
        isVisibleText: true, textText: td("chaHeroText"), fontSizeText: "text-lg", textAlignText: "left", textColor: MUTE,
        secondaryButtonSize: "medium", secondaryButtonText: td("chaHeroSecondaryButton"), isVisibleSecondaryButton: true,
        secondaryButtonBorder: true, secondaryButtonUnderline: false,
        primaryButtonSize: "medium", primaryButtonText: td("chaHeroPrimaryButton"), isVisiblePrimaryButton: true,
        primaryButtonBorder: true, primaryButtonUnderline: false,
        backgroundType: "image", imageRounded: true, backgroundOverlayColor: "transparent",
        imageSrc: `${W}/hero-einladung.jpg`, backgroundColor: "#fefcf8",
      },
      displayName: "Hero", custom: { icon: "IconSlideshow", preview: pv("Hero-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "cha_hero_eb", header: "cha_hero_hd", text: "cha_hero_tx", buttonOne: "cha_hero_b1", buttonTwo: "cha_hero_b2" },
    },
    cha_hero_eb: tc(HERO, { isVisible: true, text: td("chaHeroEyebrow"), fontSize: "text-xs", textAlign: "left", displayName: "headline", color: ACCENT, header: true }),
    cha_hero_hd: tc(HERO, { isVisible: true, text: td("chaHeroHeader"), fontSize: "text-6xl", textAlign: "left", displayName: "header", color: INK, header: true }),
    cha_hero_tx: tc(HERO, { isVisible: true, text: td("chaHeroText"), fontSize: "text-lg", textAlign: "left", displayName: "text", color: MUTE, header: false }),
    cha_hero_b1: btn(HERO, { type: "secondary", text: td("chaHeroSecondaryButton"), displayName: "buttonOne" }),
    cha_hero_b2: btn(HERO, { type: "primary", linkType: "internal", linkUrl: "#" + DI, text: td("chaHeroPrimaryButton"), displayName: "buttonTwo" }),

    // ── Numbers ──
    [NUMBERS]: {
      type: { resolvedName: "Numbers" }, isCanvas: true,
      props: {
        sectionHeight: 60, contentAlignment: "center", sectionMargin: SEC_MARGIN, gridColumns: "4",
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextEntries: "gap-8 wb-md:gap-12", distEntries: "gap-5 wb-md:gap-6", isVisible: true, layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("chaNumbersHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("chaNumbersHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("chaNumbersText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Numbers", custom: { icon: "Icon123", preview: pv("NumbersPreview"), allowedComponents: ["NumbersChild"] },
      parent: B, hidden: false, nodes: ["cha_num1", "cha_num2", "cha_num3", "cha_num4"],
      linkedNodes: { headline: "cha_num_eb", header: "cha_num_hd", text: "cha_num_tx" },
    },
    cha_num_eb: tc(NUMBERS, { isVisible: false, text: td("chaNumbersHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    cha_num_hd: tc(NUMBERS, { isVisible: true, text: td("chaNumbersHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    cha_num_tx: tc(NUMBERS, { isVisible: true, text: td("chaNumbersText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    cha_num1: numbersChild(NUMBERS, 0, td),
    cha_num2: numbersChild(NUMBERS, 1, td),
    cha_num3: numbersChild(NUMBERS, 2, td),
    cha_num4: numbersChild(NUMBERS, 3, td),

    // ── Agenda ──
    [AGENDA]: {
      type: { resolvedName: "Agenda" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextEntries: "gap-8 wb-md:gap-12", distEntries: "space-y-3 wb-md:space-y-4", sectionMargin: SEC_MARGIN, isVisible: true, layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("chaAgendaHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("chaAgendaHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("chaAgendaText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Agenda", custom: { icon: "IconListCheck", preview: pv("AgendaPreview"), allowedComponents: ["AgendaChild"] },
      parent: B, hidden: false, nodes: ["cha_ag1", "cha_ag2", "cha_ag3", "cha_ag4", "cha_ag5"],
      linkedNodes: { headline: "cha_ag_eb", header: "cha_ag_hd", text: "cha_ag_tx" },
    },
    cha_ag_eb: tc(AGENDA, { isVisible: false, text: td("chaAgendaHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    cha_ag_hd: tc(AGENDA, { isVisible: true, text: td("chaAgendaHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    cha_ag_tx: tc(AGENDA, { isVisible: true, text: td("chaAgendaText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    cha_ag1: agendaChild(AGENDA, 0, td),
    cha_ag2: agendaChild(AGENDA, 1, td),
    cha_ag3: agendaChild(AGENDA, 2, td),
    cha_ag4: agendaChild(AGENDA, 3, td),
    cha_ag5: agendaChild(AGENDA, 4, td),

    // ── Speaker (Patrons) — hidden by default (no portrait library) ──
    [SPEAKER]: {
      type: { resolvedName: "Speaker" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextSpeakers: "gap-8 wb-md:gap-12", distSpeakers: "gap-5 wb-md:gap-6", sectionMargin: SEC_MARGIN, isVisible: false, gridColumns: "3", layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("chaSpeakerHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("chaSpeakerHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("chaSpeakerText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Speaker", custom: { icon: "IconUserStar", preview: pv("Speaker-preview"), allowedComponents: ["SpeakerChild"] },
      parent: B, hidden: false, nodes: ["cha_sp1", "cha_sp2", "cha_sp3", "cha_sp4", "cha_sp5", "cha_sp6"],
      linkedNodes: { headline: "cha_sp_eb", header: "cha_sp_hd", text: "cha_sp_tx" },
    },
    cha_sp_eb: tc(SPEAKER, { isVisible: false, text: td("chaSpeakerHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    cha_sp_hd: tc(SPEAKER, { isVisible: true, text: td("chaSpeakerHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    cha_sp_tx: tc(SPEAKER, { isVisible: true, text: td("chaSpeakerText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    cha_sp1: speakerChild(SPEAKER, 0, td),
    cha_sp2: speakerChild(SPEAKER, 1, td),
    cha_sp3: speakerChild(SPEAKER, 2, td),
    cha_sp4: speakerChild(SPEAKER, 3, td),
    cha_sp5: speakerChild(SPEAKER, 4, td),
    cha_sp6: speakerChild(SPEAKER, 5, td),

    // ── Text (note from host) ──
    [TEXT]: {
      type: { resolvedName: "TextSection" }, isCanvas: false,
      props: {
        buttonAlign: "center", padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButtons: "gap-6 wb-md:gap-8", distButtons: "gap-3 wb-md:gap-4", sectionMargin: SEC_MARGIN, isVisible: true, layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("chaTextHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("chaTextHeader"), fontSizeHeader: "text-4xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("chaTextBody"), fontSizeText: "text-md", textAlignText: "center",
        secondaryButtonSize: "medium", secondaryButtonText: td("chaTextSecondaryButton"), isVisibleSecondaryButton: true,
        secondaryButtonBorder: true, secondaryButtonUnderline: false, secondaryButtonAlign: "center",
        primaryButtonSize: "medium", primaryButtonText: td("chaTextPrimaryButton"), isVisiblePrimaryButton: true,
        primaryButtonBorder: true, primaryButtonUnderline: false, primaryButtonAlign: "center",
      },
      displayName: "Text", custom: { icon: "IconAlignJustified", preview: pv("textPreview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "cha_tx_eb", header: "cha_tx_hd", text: "cha_tx_tx", buttonOne: "cha_tx_b1", buttonTwo: "cha_tx_b2" },
    },
    cha_tx_eb: tc(TEXT, { isVisible: false, text: td("chaTextHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    cha_tx_hd: tc(TEXT, { isVisible: true, text: td("chaTextHeader"), fontSize: "text-4xl", textAlign: "center", displayName: "header", header: true }),
    cha_tx_tx: tc(TEXT, { isVisible: true, text: td("chaTextBody"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    cha_tx_b1: btn(TEXT, { type: "secondary", text: td("chaTextSecondaryButton"), displayName: "buttonOne" }),
    cha_tx_b2: btn(TEXT, { type: "primary", text: td("chaTextPrimaryButton"), displayName: "buttonTwo" }),

    // ── Image (wildflowers, image left / text right) ──
    [IMAGE]: {
      type: { resolvedName: "Image" }, isCanvas: false,
      props: {
        sectionHeight: 70, contentAlignment: "center", sectionMargin: SEC_MARGIN, inversed: true,
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButton: "mb-6 wb-md:mb-8", distContentImage: "gap-6 wb-md:gap-10 wb-lg:gap-16", isVisible: true,
        isVisibleHeadline: false, textHeadline: td("chaImageHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "left",
        isVisibleHeader: true, textHeader: td("chaImageHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "left",
        isVisibleText: true, textText: td("chaImageText"), fontSizeText: "text-md", textAlignText: "left",
        buttonSize: "medium", buttonText: td("chaImageButton"), isVisibleButton: true, buttonAlign: "left",
        buttonBorder: true, buttonUnderline: false,
        imageRounded: "rounded-lg", layoutWidth: "50%",
        isVisibleImage: true, imageSrc: `${W}/detail-wildflowers.jpg`, imageAspectRatio: "aspect-[4/5]",
      },
      displayName: "Image", custom: { icon: "IconSection", preview: pv("Image-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "cha_im_eb", header: "cha_im_hd", text: "cha_im_tx", button: "cha_im_btn", image: "cha_im_img" },
    },
    cha_im_eb: tc(IMAGE, { isVisible: false, text: td("chaImageHeader"), fontSize: "text-lg", textAlign: "left", displayName: "headline", header: true }),
    cha_im_hd: tc(IMAGE, { isVisible: true, text: td("chaImageHeader"), fontSize: "text-3xl", textAlign: "left", displayName: "header", header: true }),
    cha_im_tx: tc(IMAGE, { isVisible: true, text: td("chaImageText"), fontSize: "text-md", textAlign: "left", displayName: "text", header: false }),
    cha_im_btn: btn(IMAGE, { text: td("chaImageButton"), displayName: "button" }),
    cha_im_img: {
      type: { resolvedName: "ImageChild" }, isCanvas: false,
      props: { src: `${W}/detail-wildflowers.jpg`, isVisible: true, aspectRatio: "aspect-[4/5]", displayName: "ImageChild", width: "w-full wb-lg:w-1/2", canDuplicate: false },
      displayName: "ImageChild", custom: { icon: "IconSection" },
      parent: IMAGE, hidden: false, nodes: [], linkedNodes: {},
    },

    // ── Countdown ──
    [COUNTDOWN]: {
      type: { resolvedName: "Countdown" }, isCanvas: false,
      props: {
        sectionHeight: 40, contentAlignment: "center", sectionMargin: SEC_MARGIN, layoutWidth: "50%",
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButton: "mb-6 wb-md:mb-8", distButtonCountdown: "gap-6 wb-md:gap-10 wb-lg:gap-16", isVisible: true, buttonAlign: "left",
        isVisibleHeadline: true, textHeadline: td("chaCountdownEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "left",
        isVisibleHeader: true, textHeader: td("chaCountdownHeader"), fontSizeHeader: "text-4xl", textAlignHeader: "left",
        isVisibleText: true, textText: td("chaCountdownText"), fontSizeText: "text-base", textAlignText: "left",
        buttonSize: "medium", buttonText: td("chaCountdownButton"), isVisibleButton: true, buttonBorder: true, buttonUnderline: false,
        isVisiblCountdown: true, countdownSizeDate: "text-5xl", countdownFontTypeDate: SERIF, countdownFontColorDate: INK,
        countdownSize: "text-base", countdownFontColor: "#8a8270", countdownBorderColor: "#d4ccbf",
      },
      displayName: "Countdown", custom: { icon: "IconAlarm", preview: pv("Countdown-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "cha_cd_eb", header: "cha_cd_hd", text: "cha_cd_tx", button: "cha_cd_btn" },
    },
    cha_cd_eb: tc(COUNTDOWN, { isVisible: true, text: td("chaCountdownEyebrow"), fontSize: "text-xs", textAlign: "left", displayName: "headline", color: ACCENT, header: true }),
    cha_cd_hd: tc(COUNTDOWN, { isVisible: true, text: td("chaCountdownHeader"), fontSize: "text-4xl", textAlign: "left", displayName: "header", color: INK, header: true }),
    cha_cd_tx: tc(COUNTDOWN, { isVisible: true, text: td("chaCountdownText"), fontSize: "text-base", textAlign: "left", displayName: "text", color: MUTE, header: false }),
    cha_cd_btn: btn(COUNTDOWN, { text: td("chaCountdownButton"), displayName: "ButtonChild" }),

    // ── Gallery — hidden by default (no photo library) ──
    [GALLERY]: {
      type: { resolvedName: "Gallery" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButtons: "gap-6 wb-md:gap-8", distButtons: "gap-3 wb-md:gap-4", distButtonGallery: "gap-3 wb-md:gap-4", distEntries: "gap-5 wb-md:gap-6", sectionMargin: SEC_MARGIN, isVisible: false, buttonAlign: "center", gridColumns: "4", layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("chaGalleryHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("chaGalleryHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: false, textText: td("chaGalleryHeader"), fontSizeText: "text-md", textAlignText: "center",
        secondaryButtonSize: "medium", secondaryButtonText: td("chaGallerySecondaryButton"), isVisibleSecondaryButton: true,
        secondaryButtonBorder: true, secondaryButtonUnderline: false,
        primaryButtonSize: "medium", primaryButtonText: td("chaGalleryPrimaryButton"), isVisiblePrimaryButton: true,
        primaryButtonBorder: true, primaryButtonUnderline: false,
      },
      displayName: "Gallery", custom: { icon: "IconPhoto", preview: pv("Gallery-preview"), allowedComponents: ["ImageChild"] },
      parent: B, hidden: false,
      nodes: ["cha_g1", "cha_g2", "cha_g3", "cha_g4", "cha_g5", "cha_g6"],
      linkedNodes: { headline: "cha_g_eb", header: "cha_g_hd", text: "cha_g_tx", secondary: "cha_g_b2", primary: "cha_g_b1" },
    },
    cha_g_eb: tc(GALLERY, { isVisible: false, text: td("chaGalleryHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    cha_g_hd: tc(GALLERY, { isVisible: true, text: td("chaGalleryHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    cha_g_tx: tc(GALLERY, { isVisible: false, text: td("chaGalleryHeader"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    cha_g_b2: btn(GALLERY, { type: "secondary", text: td("chaGallerySecondaryButton"), displayName: "ButtonChild" }),
    cha_g_b1: btn(GALLERY, { text: td("chaGalleryPrimaryButton"), displayName: "ButtonChild" }),
    cha_g1: galleryImage(GALLERY, 0),
    cha_g2: galleryImage(GALLERY, 1),
    cha_g3: galleryImage(GALLERY, 2),
    cha_g4: galleryImage(GALLERY, 3),
    cha_g5: galleryImage(GALLERY, 4),
    cha_g6: galleryImage(GALLERY, 5),

    // ── Video (aftermovie) — present but hidden by default ──
    [VIDEO]: {
      type: { resolvedName: "Video" }, isCanvas: false,
      props: {
        sectionHeight: 70, contentAlignment: "center", sectionMargin: SEC_MARGIN, layoutWidth: "50%",
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButton: "mb-6 wb-md:mb-8", distContentVideo: "gap-6 wb-md:gap-10 wb-lg:gap-16", isVisible: false,
        isVisibleHeadline: false, textHeadline: td("chaVideoHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("chaVideoHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("chaVideoText"), fontSizeText: "text-md", textAlignText: "center",
        buttonSize: "medium", buttonText: td("chaVideoButton"), isVisibleButton: true, buttonBorder: true, buttonUnderline: false, buttonAlign: "center",
        videoAspectRatio: "aspect-video", isVisibleVideo: true, videoUrl: "https://youtu.be/sdESKm-nKQE",
      },
      displayName: "Video", custom: { icon: "IconVideo", preview: pv("Video-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "cha_vd_eb", header: "cha_vd_hd", text: "cha_vd_tx", button: "cha_vd_btn" },
    },
    cha_vd_eb: tc(VIDEO, { isVisible: false, text: td("chaVideoHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    cha_vd_hd: tc(VIDEO, { isVisible: true, text: td("chaVideoHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    cha_vd_tx: tc(VIDEO, { isVisible: true, text: td("chaVideoText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    cha_vd_btn: btn(VIDEO, { text: td("chaVideoButton"), isVisible: true, displayName: "button" }),

    // ── Testimonials (dark surface) ──
    [TESTI]: {
      type: { resolvedName: "Testimonials" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextTestimonials: "gap-8 wb-md:gap-12", distTestimonials: "gap-5 wb-md:gap-6", sectionMargin: SEC_MARGIN, isVisible: true, gridColumns: "3", layoutWidth: "100%",
        backgroundColor: DARK,
        isVisibleHeadline: true, textHeadline: td("chaTestimonialsEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("chaTestimonialsHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: false, textText: td("chaTestimonialsHeader"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Testimonials", custom: { icon: "IconQuote", preview: pv("Testimonials-preview"), allowedComponents: ["TestimonialsChild"] },
      parent: B, hidden: false, nodes: ["cha_ts1", "cha_ts2", "cha_ts3"],
      linkedNodes: { headline: "cha_ts_eb", header: "cha_ts_hd", text: "cha_ts_tx" },
    },
    cha_ts_eb: tc(TESTI, { isVisible: true, text: td("chaTestimonialsEyebrow"), fontSize: "text-xs", textAlign: "center", displayName: "headline", color: ON_DARK_ACCENT, header: true }),
    cha_ts_hd: tc(TESTI, { isVisible: true, text: td("chaTestimonialsHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", color: "#ffffff", header: true }),
    cha_ts_tx: tc(TESTI, { isVisible: false, text: td("chaTestimonialsHeader"), fontSize: "text-md", textAlign: "center", displayName: "text", color: ON_DARK, header: false }),
    cha_ts1: testimonialChild(TESTI, 0, td),
    cha_ts2: testimonialChild(TESTI, 1, td),
    cha_ts3: testimonialChild(TESTI, 2, td),

    // ── Blog (Logbuch) — present but hidden by default ──
    [BLOG]: {
      type: { resolvedName: "Blog" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextEntries: "gap-8 wb-md:gap-12", distEntries: "gap-5 wb-md:gap-6", sectionMargin: SEC_MARGIN, isVisible: false, gridColumns: "3", layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("chaBlogHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("chaBlogHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("chaBlogText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Blog", custom: { icon: "IconClick", preview: pv("Blog-preview"), allowedComponents: ["BlogChild"] },
      parent: B, hidden: false, nodes: ["cha_bl1", "cha_bl2", "cha_bl3"],
      linkedNodes: { headline: "cha_bl_eb", header: "cha_bl_hd", text: "cha_bl_tx" },
    },
    cha_bl_eb: tc(BLOG, { isVisible: false, text: td("chaBlogHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    cha_bl_hd: tc(BLOG, { isVisible: true, text: td("chaBlogHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    cha_bl_tx: tc(BLOG, { isVisible: true, text: td("chaBlogText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    cha_bl1: blogChild(BLOG, 0, td),
    cha_bl2: blogChild(BLOG, 1, td),
    cha_bl3: blogChild(BLOG, 2, td),

    // ── FaQ ──
    [FAQ]: {
      type: { resolvedName: "FaQ" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextEntries: "gap-8 wb-md:gap-12", distEntries: "space-y-3 wb-md:space-y-4", sectionMargin: SEC_MARGIN, isVisible: true, layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("chaFaqHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("chaFaqHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("chaFaqText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "FaQ", custom: { icon: "IconQuestionMark", preview: pv("faqPreview"), allowedComponents: ["FaQChild"] },
      parent: B, hidden: false, nodes: ["cha_fq1", "cha_fq2", "cha_fq3", "cha_fq4", "cha_fq5", "cha_fq6"],
      linkedNodes: { headline: "cha_fq_eb", header: "cha_fq_hd", text: "cha_fq_tx" },
    },
    cha_fq_eb: tc(FAQ, { isVisible: false, text: td("chaFaqHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    cha_fq_hd: tc(FAQ, { isVisible: true, text: td("chaFaqHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    cha_fq_tx: tc(FAQ, { isVisible: true, text: td("chaFaqText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    cha_fq1: faqChild(FAQ, 0, td),
    cha_fq2: faqChild(FAQ, 1, td),
    cha_fq3: faqChild(FAQ, 2, td),
    cha_fq4: faqChild(FAQ, 3, td),
    cha_fq5: faqChild(FAQ, 4, td),
    cha_fq6: faqChild(FAQ, 5, td),

    // ── Map (Hof Naturell) — present but hidden by default ──
    [MAP]: {
      type: { resolvedName: "Map" }, isCanvas: false,
      props: {
        sectionHeight: 70, contentAlignment: "center", sectionMargin: SEC_MARGIN, layoutWidth: "50%",
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButton: "mb-6 wb-md:mb-8", distContentMap: "gap-6 wb-md:gap-10 wb-lg:gap-16", isVisible: false,
        isVisibleHeadline: false, textHeadline: td("chaMapHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "left",
        isVisibleHeader: true, textHeader: td("chaMapHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "left",
        isVisibleText: true, textText: td("chaMapText"), fontSizeText: "text-md", textAlignText: "left",
        buttonSize: "medium", buttonText: td("chaMapButton"), isVisibleButton: true, buttonBorder: true, buttonUnderline: false, buttonAlign: "left",
        mapRounded: "rounded-lg",
        mapZoom: 12, mapType: "m", aspectRatio: "aspect-[4/3]", isVisibleMap: true, mapLocation: td("chaMapLocation"),
      },
      displayName: "Map", custom: { icon: "IconMap2", preview: pv("Map-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "cha_mp_eb", header: "cha_mp_hd", text: "cha_mp_tx", button: "cha_mp_btn" },
    },
    cha_mp_eb: tc(MAP, { isVisible: false, text: td("chaMapHeader"), fontSize: "text-lg", textAlign: "left", displayName: "headline", header: true }),
    cha_mp_hd: tc(MAP, { isVisible: true, text: td("chaMapHeader"), fontSize: "text-3xl", textAlign: "left", displayName: "header", header: true }),
    cha_mp_tx: tc(MAP, { isVisible: true, text: td("chaMapText"), fontSize: "text-md", textAlign: "left", displayName: "text", header: false }),
    cha_mp_btn: btn(MAP, { text: td("chaMapButton"), displayName: "button" }),

    // ── DataInput (multi-step registration / donation) ──
    [DI]: {
      type: { resolvedName: "DataInput" }, isCanvas: false,
      props: {
        padding: PAD, sectionMargin: SEC_MARGIN, isVisible: true, textSpacing: DIST_CARD,
        backgroundColor: "transparent", border: false, borderWidth: "border-2", borderStyle: "border-solid",
        borderSides: ["bottom"], borderRadius: undefined,
        isVisibleHeadline: true, textHeadline: td("chaDataHeadline"), fontSizeHeadline: "text-base", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("chaDataHeader"), fontSizeHeader: "text-lg", textAlignHeader: "center",
        isVisibleText: true, textText: td("chaDataText"), fontSizeText: "text-sm", textAlignText: "center",
        allowUpdate: true,
        multiStepEnabled: true, stepLabels: [td("chaStepStatus"), td("chaStepDonation"), td("chaStepProfile"), td("chaStepOverview")], multiStepMode: "pages",
        progressBarEnabled: true, progressBarColor: ACCENT,
        overviewEnabled: true, stepSections: [["fields-0"], ["fields-1"], ["fields-2"], []],
      },
      displayName: "DataInput", custom: { icon: "IconForms", preview: pv("DataInput-Preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: {
        headline: "cha_di_hl", header: "cha_di_hd", text: "cha_di_tx",
        fieldsGroup0: "cha_di_f0", fieldsGroup1: "cha_di_f1", fieldsGroup2: "cha_di_f2",
        fieldsGroup3: "cha_di_f3", fieldsGroup4: "cha_di_f4",
        companionsGroup: "cha_di_comp", overviewGroup: "cha_di_ov", checkboxes: "cha_di_cb",
        button: "cha_di_btn", backButton: "cha_di_back", nextButton: "cha_di_next",
      },
    },
    cha_di_hl: tc(DI, { isVisible: true, text: td("chaDataHeadline"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-base", textAlign: "center", displayName: "headline", header: true }),
    cha_di_hd: tc(DI, { isVisible: true, text: td("chaDataHeader"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-lg", textAlign: "center", displayName: "header", header: true }),
    cha_di_tx: tc(DI, { isVisible: true, text: td("chaDataText"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-sm", textAlign: "center", displayName: "text", header: false }),
    cha_di_f0: {
      type: { resolvedName: "FieldsGroup" }, isCanvas: true,
      props: { isVisible: true, fieldCount: 4, groupIndex: 0, fieldSpacing: "medium" },
      displayName: "FieldsGroup", custom: { icon: "IconForms", allowedComponents: ["FormInputChild"] },
      parent: DI, hidden: false, nodes: ["cha_di_acceptance", "cha_di_first", "cha_di_last", "cha_di_email"], linkedNodes: {},
    },
    // FIRST in the group, and filter-free — the question every other
    // field in this group is gated on. See `acceptanceFi`.
    cha_di_acceptance: acceptanceFi("cha_di_f0", td("acceptanceState")),
    cha_di_first: fi("cha_di_f0", { label: td("firstName"), requiredField: true, inputFieldVariable: "firstName", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER }),
    cha_di_last: fi("cha_di_f0", { label: td("lastName"), requiredField: true, inputFieldVariable: "lastName", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER }),
    cha_di_email: fi("cha_di_f0", { label: td("email"), requiredField: true, inputFieldVariable: "email", gridArea: "col-span-12", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER }),
    cha_di_f1: {
      type: { resolvedName: "FieldsGroup" }, isCanvas: true,
      props: { isVisible: true, fieldCount: 2, groupIndex: 1, fieldSpacing: "medium" },
      displayName: "FieldsGroup", custom: { icon: "IconForms", allowedComponents: ["FormInputChild"] },
      parent: DI, hidden: false, nodes: ["cha_di_amount", "cha_di_anon"], linkedNodes: {},
    },
    cha_di_amount: fi("cha_di_f1", { label: td("chaFieldDonation"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "donationAmount", changeable: true }),
    cha_di_anon: fi("cha_di_f1", { label: td("chaFieldAnonymous"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "anonymous", changeable: true }),
    cha_di_f2: {
      type: { resolvedName: "FieldsGroup" }, isCanvas: true,
      props: { isVisible: true, fieldCount: 1, groupIndex: 2, fieldSpacing: "medium" },
      displayName: "FieldsGroup", custom: { icon: "IconForms", allowedComponents: ["FormInputChild"] },
      parent: DI, hidden: false, nodes: ["cha_di_diet"], linkedNodes: {},
    },
    cha_di_diet: fi("cha_di_f2", { label: td("chaFieldDiet"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "diet", multiline: true, gridArea: "col-span-12", changeable: true }),
    cha_di_f3: emptyFieldsGroup(DI, 3),
    cha_di_f4: emptyFieldsGroup(DI, 4),
    cha_di_comp: {
      type: { resolvedName: "CompanionsGroup" }, isCanvas: true,
      props: { isVisible: false, fieldCount: 1, fieldSpacing: "medium", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER },
      displayName: "CompanionsGroup", custom: { icon: "IconUsers", allowedComponents: ["FormInputChild"] },
      parent: DI, hidden: false, nodes: ["cha_di_comp_first"], linkedNodes: {},
    },
    cha_di_comp_first: fi("cha_di_comp", { label: td("firstName"), requiredField: false, inputFieldVariable: "firstName", gridArea: "col-span-12", changeable: true, type: "companion" }),
    cha_di_ov_t: tc("cha_di_ov", { isVisible: true, text: td("overviewTitle"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-base", textAlign: "left", displayName: "overviewTitle", header: true }),
    cha_di_ov_x: tc("cha_di_ov", { isVisible: true, text: td("overviewText"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-sm", textAlign: "left", displayName: "overviewText", header: false }),
    cha_di_ov: {
      type: { resolvedName: "OverviewGroup" }, isCanvas: true,
      props: { isVisible: false, parentNodeId: DI, stepSections: [["fields-0"], ["fields-1"], ["fields-2"], []], stepLabels: [] },
      displayName: "summaryGroup", custom: { icon: "IconListDetails" },
      parent: DI, hidden: false, nodes: ["cha_di_ov_t", "cha_di_ov_x"], linkedNodes: {},
    },
    cha_di_cb: {
      type: { resolvedName: "Checkboxes" }, isCanvas: false,
      props: { isVisible: true, checkboxes: toCheckboxItems(defaultRegistrationConsents(td)), displayName: "checkboxes" },
      displayName: "Checkboxes", custom: { icon: "IconCheckbox" },
      parent: DI, hidden: false, nodes: [], linkedNodes: {},
    },
    cha_di_btn: btn(DI, { linkType: "internal", linkUrl: "", text: td("chaDataSubmit"), submittedText: "", isSubmitted: false, enableSubmittedState: true, size: "small", widthMode: "full", buttonType: "submit", margin: BTN_FORM_SP, displayName: "button" }),
    cha_di_back: btn(DI, { linkType: "internal", type: "secondary", linkUrl: "", text: td("back"), navigationRole: "back", size: "small", widthMode: "content", isVisible: false, displayName: "backButton" }),
    cha_di_next: btn(DI, { linkType: "internal", linkUrl: "", text: td("next"), navigationRole: "next", size: "small", widthMode: "content", isVisible: false, displayName: "nextButton" }),

    // ── Contact ──
    [CONTACT]: {
      type: { resolvedName: "Contact" }, isCanvas: true,
      props: {
        sectionHeight: 70, contentAlignment: "center", infoSection: true, backgroundColor: "transparent",
        padding: PAD, sectionMargin: SEC_MARGIN, textSpacing: "space-y-3 wb-md:space-y-5 wb-lg:space-y-6", isVisible: true,
        infoWidth: "50%", inversed: false,
        isVisibleHeadline: true, textHeadline: td("chaContactEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "left",
        isVisibleHeader: true, textHeader: td("chaContactHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "left",
        isVisibleText: true, textText: td("chaContactText"), fontSizeText: "text-md", textAlignText: "left",
        isSocialVisible: true, socialIcons: [{ platform: "instagram", url: "https://instagram.com" }, { platform: "facebook", url: "https://facebook.com" }], socialSize: 24, socialPosition: "left",
        buttonSize: "medium", buttonText: td("chaContactButton"), isVisibleButton: true, buttonBorder: true, buttonUnderline: false,
      },
      displayName: "Contact", custom: { icon: "IconForms", preview: pv("Contact-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: {
        headline: "cha_ct_eb", header: "cha_ct_hd", text: "cha_ct_tx",
        firstName: "cha_ct_first", lastName: "cha_ct_last", eMail: "cha_ct_email",
        phoneNumber: "cha_ct_phone", Message: "cha_ct_msg", button: "cha_ct_btn",
      },
    },
    cha_ct_eb: tc(CONTACT, { isVisible: true, text: td("chaContactEyebrow"), fontSize: "text-xs", textAlign: "left", displayName: "headline", color: ACCENT, header: true }),
    cha_ct_hd: tc(CONTACT, { isVisible: true, text: td("chaContactHeader"), fontSize: "text-3xl", textAlign: "left", displayName: "header", header: true }),
    cha_ct_tx: tc(CONTACT, { isVisible: true, text: td("chaContactText"), fontSize: "text-md", textAlign: "left", displayName: "text", header: false }),
    cha_ct_first: fi(CONTACT, { label: td("firstName"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "firstName", changeable: false }),
    cha_ct_last: fi(CONTACT, { label: td("lastName"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "lastName", changeable: false }),
    cha_ct_email: fi(CONTACT, { label: td("email"), overwriteType: "EMAIL", requiredField: true, inputFieldVariable: "email", changeable: false, locked: true }),
    cha_ct_phone: fi(CONTACT, { label: td("phoneNumber"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "phone", changeable: false }),
    cha_ct_msg: fi(CONTACT, { label: td("messageLabel"), overwriteType: "STRING", requiredField: true, inputFieldVariable: "message", multiline: true, gridArea: "col-span-12", changeable: false, locked: true }),
    cha_ct_btn: btn(CONTACT, { linkType: "internal", linkUrl: "", text: td("chaContactButton"), widthMode: "full", buttonType: "submit", displayName: "button", fixedNavigation: false }),
  });
};

// ── Meta sections (AppBar + Footer) ───────────────────────────────────

/** White bar, forest-green rules, logo + nav + "Tisch sichern" CTA. */
export const charityAppbar = (td: TranslationFn): string =>
  enc({
    type: { resolvedName: "AppBarSection" }, isCanvas: false,
    props: {
      padding: "p-4 wb-md:p-5 wb-lg:p-6", isVisible: true,
      backgroundColor: "#ffffff",
      border: true, borderColor: ACCENT, borderWidth: "border", borderStyle: "border-solid", borderSides: ["bottom"],
      sectionOrder: ["logo", "links", "social"],
      isLogoVisible: true, logoSrc: LOGO, logoWidth: "w-32", logoHeight: "h-10", logoPosition: "left",
      isLinksVisible: true, linksPosition: "center",
      links: [
        { label: td("chaLinkProgram"), url: "#cha_agenda", linkType: "internal" },
        { label: td("chaLinkPatrons"), url: "#cha_speaker", linkType: "internal" },
        { label: td("chaLinkFaq"), url: "#cha_faq", linkType: "internal" },
        { label: td("chaLinkContact"), url: "#cha_contact", linkType: "internal" },
      ],
      linksColor: INK, linksFontSize: "text-sm", linksFontWeight: "font-medium",
      isSocialVisible: false, socialIcons: [], socialPosition: "right", socialSize: 24,
      logoutButtonSize: "small", logoutButtonText: td("chaHeroPrimaryButton"),
      logoutButtonBorder: true, logoutButtonBackgroundColor: ACCENT, logoutButtonColor: "#ffffff", logoutButtonBorderColor: ACCENT,
    },
    displayName: "AppBar", custom: { icon: "IconLayoutNavbar" },
    parent: "DCH9WIIT55", hidden: false, nodes: [], linkedNodes: {},
  });

/** Dark warm footer, forest-green top rule, four link groups + social. No logo. */
export const charityFooter = (td: TranslationFn): string =>
  enc({
    type: { resolvedName: "FooterSection" }, isCanvas: false,
    props: {
      padding: "px-4 py-10 wb-md:px-6 wb-lg:px-8", isVisible: true,
      backgroundColor: DARK,
      border: true, borderColor: ACCENT, borderWidth: "border-4", borderStyle: "border-solid", borderSides: ["top"],
      sectionOrder: ["links", "social"],
      isLogoVisible: false, logoSrc: LOGO, logoWidth: "w-32", logoHeight: "h-auto",
      isLinkGroupsVisible: true,
      linkGroups: [
        { title: td("chaFooterProgram"), links: [
          { label: td("chaLinkProgram"), url: "#cha_agenda", linkType: "internal" },
          { label: td("chaLinkPatrons"), url: "#cha_speaker", linkType: "internal" },
          { label: td("chaLinkGallery"), url: "#cha_gallery", linkType: "internal" },
        ] },
        // Event practicalities — Maps + add-to-calendar are EXTERNAL (new tab).
        // URLs are placeholders the organiser edits per event.
        { title: td("chaFooterEvent"), links: [
          { label: td("chaLinkLocation"), url: "https://www.google.com/maps/search/?api=1&query=Walthausen", linkType: "external" },
          { label: td("chaLinkCalendar"), url: "https://calendar.google.com/calendar/render?action=TEMPLATE&text=Benefiz-Dinner", linkType: "external" },
          { label: td("chaLinkTickets"), url: "/register", linkType: "internal" },
        ] },
        { title: td("chaFooterSupport"), links: [
          { label: td("chaLinkContact"), url: "#cha_contact", linkType: "internal" },
          { label: td("chaLinkFaq"), url: "#cha_faq", linkType: "internal" },
          { label: td("chaLinkDonate"), url: "/register", linkType: "internal" },
        ] },
        { title: td("chaFooterLegal"), links: [
          { label: td("chaLinkPrivacy"), url: "/privacy", linkType: "internal" },
          { label: td("chaLinkImprint"), url: "/imprint", linkType: "internal" },
          { label: td("chaLinkTerms"), url: "/terms", linkType: "internal" },
        ] },
      ],
      linksTitleColor: "#ffffff", linksTitleFontSize: "text-sm", linksTitleFontWeight: "font-semibold",
      linksColor: "#a8a08e", linksFontSize: "text-sm", linksFontWeight: "font-normal", linksHoverColor: "hover:font-primary",
      isSocialVisible: true,
      socialIcons: [
        { altText: "instagram", platform: "instagram", url: "https://instagram.com" },
        { altText: "facebook", platform: "facebook", url: "https://facebook.com" },
      ],
      socialColor: "#a8a08e", socialHoverColor: "hover:font-primary", socialSize: 20, socialTitle: td("followUs"),
      isCopyrightVisible: true, copyrightColor: "#8a8270", copyrightFontSize: "text-xs", copyrightCompanyName: td("yourCompany"),
    },
    displayName: "Footer", custom: { icon: "IconMenu2" },
    parent: "xaUW2UDZT8", hidden: false, nodes: [], linkedNodes: {},
  });

// ── Style preset (naturell-barn) ──────────────────────────────────────

const CREAM = "#fefcf8"; // warm cream page surface
const SOFT_GREEN = "#c4d5b8"; // soft green secondary
const SANS = "Inter, 'Helvetica Neue', Arial, sans-serif";

// ── Theme CSS (P9 WS4-B3) ─────────────────────────────────────────────
//
// Marker comment first so a later user edit is distinguishable from the
// pristine preset. Composed from the shared premium-css builders (WS3).
// The hero stays a light naturell sheet (image right, node props); the css
// layers the community-light treatment on top: two soft leaf shapes in the
// template's greens drifting in the corners, a gentle green arc, uppercase
// letterspaced kickers, and the self-hosted faces so the preset's
// Cormorant Garamond/Inter stacks actually resolve.
const CUSTOM_CSS = `/* theme:charity */
${fontFaces([
  { family: "Cormorant Garamond", file: "cormorant-garamond" },
  { family: "Inter", file: "inter-regular", weightRange: "400" },
  { family: "Inter", file: "inter-bold", weightRange: "700" },
])}

/* Naturell hero — clip + isolate the leaf ornament layers */
#cha_hero {
${heroShell}
}

/* Soft leaf shapes in the template's greens (kept gentle — the sheet stays
   cream; border-radius 0/50% gives the organic leaf silhouette) */
#cha_hero::before,
#cha_hero::after {
${decorLayer}
  border-radius: 0 50% 0 50%;
}
#cha_hero::before {
  width: 260px;
  height: 260px;
  top: -90px;
  right: -70px;
  transform: rotate(18deg);
  background: linear-gradient(135deg, rgba(196, 213, 184, 0.5) 0%, rgba(196, 213, 184, 0.12) 70%, rgba(196, 213, 184, 0) 100%);
}
#cha_hero::after {
  width: 180px;
  height: 180px;
  bottom: -60px;
  left: -50px;
  transform: rotate(-160deg);
  background: linear-gradient(135deg, rgba(61, 90, 61, 0.16) 0%, rgba(61, 90, 61, 0.05) 70%, rgba(61, 90, 61, 0) 100%);
}

/* Uppercase letterspaced kickers (the text-xs eyebrows in each section) */
#cha_hero .text-xs, #cha_numbers .text-xs, #cha_agenda .text-xs, #cha_speaker .text-xs,
#cha_text .text-xs, #cha_image .text-xs, #cha_countdown .text-xs, #cha_gallery .text-xs,
#cha_video .text-xs, #cha_testimonials .text-xs, #cha_blog .text-xs, #cha_faq .text-xs,
#cha_map .text-xs, #cha_contact .text-xs {
  text-transform: uppercase;
  letter-spacing: 0.22em;
}

${motionGate(`${fadeUpKeyframes("cha", 18)}
  @keyframes chaRise {
    from { opacity: 0; transform: translateY(26px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes chaLeafIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  /* Friendly rise: the sheet lifts warmly, the leaves settle in after */
  #cha_hero { animation: chaRise 0.8s ${EASE} both; }
  #cha_hero::before { animation: chaLeafIn 1.4s ${EASE} 0.35s both; }
  #cha_hero::after { animation: chaLeafIn 1.4s ${EASE} 0.55s both; }

${revealBlock("cha", ["numbers", "datainput", "agenda", "speaker", "text", "image", "countdown", "gallery", "video", "testimonials", "blog", "faq", "map", "contact"], 30)}`)}
`;

export const charityPreset: WebsiteThemeStylePreset = {
  colorPalette: { primary: ACCENT, secondary: SOFT_GREEN, background: CREAM },
  fonts: { header: SERIF, body: SANS },
  rounding: "rounded-lg",
  recommendedSize: "medium",
  // Every leaf its 12 siblings declare is declared here too. Until 2026-08-20
  // this preset was the only incomplete one (audit: 12 missing leaves), so
  // applying it wrote `undefined` into those theme slots — invisible on a fresh
  // create (the resolver has fallbacks), but on the template-overwrite lane the
  // PUT skips undefined and the PREVIOUS template's value survived instead.
  // `presetCompleteness.test.ts` is the ratchet that keeps this true.
  bodyTheme: {
    fontColor: INK,
    fontSize: "16px",
    layoutOption: "full-width",
  },
  headingsTheme: {
    h1: { fontFamily: SERIF, fontSize: "48px", fontColor: INK },
    h2: { fontFamily: SERIF, fontSize: "28px", fontColor: INK },
  },
  buttonsTheme: {
    primary: { bgColor: ACCENT, bgHoverColor: "#2d4530", borderColor: ACCENT, borderHoverColor: "#2d4530", fontColor: "#ffffff", fontHoverColor: "#ffffff", fontSize: "16px", fontHoverSize: "16px", borderRadius: "rounded-lg", hoverBorderRadius: "rounded-lg" },
    // True ghost: stays transparent on hover and deepens its hairline + label
    // instead of filling (the forest-green fill is the primary's job).
    secondary: { bgColor: "transparent", bgHoverColor: "transparent", borderColor: ACCENT, borderHoverColor: "#2d4530", fontColor: ACCENT, fontHoverColor: "#2d4530", fontSize: "16px", fontHoverSize: "16px", borderRadius: "rounded-lg", hoverBorderRadius: "rounded-lg" },
  },
  hyperlinkTheme: {
    fontColor: ACCENT,
    fontHoverColor: "#2d4530",
    underline: false,
  },
  formInputTheme: {
    inputFontColor: INK,
    labelFontColor: INK,
    backgroundColor: "#ffffff",
    border: true,
    borderColor: "#d4ccbf",
    borderRadius: "rounded-lg",
    borderStyle: "border-solid",
    borderWidth: "border",
    borderSides: [],
    fontFamily: SANS,
    fontSize: "text-sm",
    errorColor: "#DC2626",
  },
  heroImage: `${W}/hero-einladung.jpg`,
  logoSrc: LOGO,
  customCss: CUSTOM_CSS,
};
